import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { beginCapture, endCapture, type CapturePresetName } from './capture-session';
import { isNative } from './platform';

export interface CapturedPhoto {
  /** 压缩后的展示用图 */
  blob: Blob;
  mimeType: string;
  width: number;
  height: number;
  /** 拍摄时刻（设备时间） */
  capturedAt: Date;
}

export interface CapturedVideo {
  blob: Blob;
  mimeType: string;
  sizeBytes: number;
  capturedAt: Date;
}

export class CameraError extends Error {
  constructor(readonly kind: 'DENIED' | 'CANCELLED' | 'FAILED' | 'TOO_LARGE') {
    super(kind);
    this.name = 'CameraError';
  }
}

/** 普通照片：长边 2048、质量 85。12MP 原图约 5MB，压完约 600KB。 */
export const NORMAL_PRESET = { maxEdge: 2048, quality: 0.85 };

/**
 * 隐蔽工程存档图：长边 4096、质量 95。
 * 这类照片是工程责任的证据，画质要保得住细节（钢筋间距、管线走向）。
 */
export const ARCHIVE_PRESET = { maxEdge: 4096, quality: 0.95 };

export interface CapturePreset {
  maxEdge: number;
  quality: number;
}

/** 按名字取档位。恢复被系统打断的那张照片时，只有名字能存进 localStorage。 */
export const PRESETS: Record<CapturePresetName, CapturePreset> = {
  normal: NORMAL_PRESET,
  archive: ARCHIVE_PRESET,
};

/**
 * 调起相机拍一张。
 *
 * **强制现场拍摄**：`source: CameraSource.Camera` 禁止从相册选图，
 * `allowEditing: false` 禁止编辑。隐蔽工程照片一旦允许翻相册，
 * 就可能拿上个月的照片充数，存档也就失去意义了（见 CLAUDE.md 约束 6）。
 *
 * `session` 给了就登记一条「正在拍照」的磁盘记录（CR-037）——
 * 安卓在相机前台时会回收我们的 Activity，回来时 WebView 是重新加载的。
 * 记录让恢复层知道这张照片该放回哪份草稿、该把人送回哪一页。
 * **不给也能拍**，只是被系统打断时恢复不了。
 */
export async function capturePhoto(
  preset: CapturePreset = NORMAL_PRESET,
  session?: { owner: string; route: string; preset: CapturePresetName },
): Promise<CapturedPhoto> {
  const capturedAt = new Date();
  if (session) beginCapture(session);
  try {
    const raw = isNative ? await captureNative() : await captureWeb();
    const processed = await downscale(raw, preset);
    return { ...processed, capturedAt };
  } finally {
    // 走到这里说明这段 JS 活到了拍照结束 —— 不管成功、取消还是报错，
    // 那条记录都没用了。**进程被杀时这行不会执行，那正是它存在的意义。**
    if (session) endCapture();
  }
}

/**
 * 把 Capacitor 回送的那张照片捞回来（CR-037）。
 *
 * Activity 被回收时，`Camera.getPhoto()` 的那个 Promise 跟着整个 JS 环境一起没了。
 * Capacitor 把插件结果存进了 `savedInstanceState`，重建后通过
 * `App` 的 `appRestoredResult` 事件交出来 —— 这是唯一能拿回那张照片的路。
 *
 * 拿到的是原图的 `webPath`，压缩还没做过，所以要重新走一遍 `downscale`。
 */
export async function photoFromRestoredResult(
  data: unknown,
  preset: CapturePreset,
  capturedAt: Date,
): Promise<CapturedPhoto | null> {
  const webPath = (data as { webPath?: unknown } | null)?.webPath;
  if (typeof webPath !== 'string' || !webPath) return null;

  try {
    const response = await fetch(webPath);
    const blob = await response.blob();
    const processed = await downscale(blob, preset);
    return { ...processed, capturedAt };
  } catch {
    // 临时文件可能已经被系统清掉了。恢复不了就算了 ——
    // 草稿里的文字仍然在，工人重拍一张即可，不用整张表重填
    return null;
  }
}

async function captureNative(): Promise<Blob> {
  try {
    const photo = await Camera.getPhoto({
      source: CameraSource.Camera, // 关键：禁止相册
      allowEditing: false, // 关键：禁止编辑
      quality: 100, // 先拿原始画质，压缩交给我们自己控制
      resultType: CameraResultType.Uri,
      saveToGallery: false, // 不往系统相册里塞，工作照片和私人照片分开
    });

    if (!photo.webPath) throw new CameraError('FAILED');
    const response = await fetch(photo.webPath);
    return await response.blob();
  } catch (error) {
    throw toCameraError(error);
  }
}

/**
 * 浏览器降级：用 file input 并带上 capture 属性。
 * 手机浏览器会直接调起相机；桌面浏览器只能选文件 —— 这是开发调试用的路径，
 * 真正的强制现场拍摄由原生分支保证。
 */
function captureWeb(): Promise<Blob> {
  return pickFile('image/*');
}

/**
 * 拉起系统相机 / 文件选择器，拿回一个文件。
 *
 * 照片走它只是降级路径，视频走它是唯一路径（见 captureVideo）。
 */
function pickFile(accept: string): Promise<File> {
  return new Promise((resolve, reject) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    input.capture = 'environment';
    input.style.display = 'none';

    let settled = false;
    const cleanup = () => {
      input.remove();
      window.removeEventListener('focus', onFocus);
    };

    // 用户在系统选择器里按了取消时不会触发 change，只能靠窗口重新获得焦点来兜底
    const onFocus = () => {
      window.setTimeout(() => {
        if (!settled) {
          settled = true;
          cleanup();
          reject(new CameraError('CANCELLED'));
        }
      }, 800);
    };

    input.addEventListener('change', () => {
      const file = input.files?.[0];
      settled = true;
      cleanup();
      if (file) resolve(file);
      else reject(new CameraError('CANCELLED'));
    });

    document.body.appendChild(input);
    window.addEventListener('focus', onFocus);
    input.click();
  });
}

/** 客户端单个视频上限。服务端是 200MB，这里留一截余量给容器封装的开销。 */
export const MAX_VIDEO_BYTES = 100 * 1024 * 1024;

/**
 * 录一段现场视频（CR-023）。
 *
 * **不装录像插件**：`@capacitor/camera` 的 `getPhoto()` 只出静态图，
 * 而社区的录像插件质量参差、维护状况不明。安卓 WebView 对
 * `<input type="file" accept="video/*" capture="environment">` 的支持是可靠的，
 * 会直接拉起系统相机录像 —— 原生和浏览器两条路合成一条，少一个依赖。
 *
 * **代价（写进了 CR-023）**：约束 6 那条「禁止相册选图」对视频**不可靠** ——
 * `capture` 属性在部分机型上仍会弹出「相机 / 文件」的选择。
 * 照片那条路不受影响，它走的是原生插件，相册是真的进不去。
 *
 * 视频不压缩：浏览器里重新编码视频要么不可行、要么慢到不能用。
 * 所以这里只能挡大小 —— 超了当场拒绝，而不是让工人传十分钟再失败。
 */
export async function captureVideo(session?: {
  owner: string;
  route: string;
}): Promise<CapturedVideo> {
  const capturedAt = new Date();
  // 录像同样会让 Activity 被回收，而且**视频拿不回来** ——
  // `<input type=file>` 没有插件回调。登记这条记录只为了把人送回那一页，
  // 让他对着还在的草稿重录一遍，而不是从空白表单重来（CR-037）
  if (session) beginCapture({ ...session, preset: 'normal' });
  try {
    const file = await pickFile('video/*');

    if (file.size > MAX_VIDEO_BYTES) {
      throw new CameraError('TOO_LARGE');
    }

    return {
      blob: file,
      // 有些机型的 File 不带 type，按最常见的容器兜底 —— 服务端的白名单认 mp4
      mimeType: file.type || 'video/mp4',
      sizeBytes: file.size,
      capturedAt,
    };
  } finally {
    if (session) endCapture();
  }
}

/**
 * 等比缩放并重新编码为 JPEG。
 *
 * 为什么一定要压：工地弱网下传一张 5MB 的原图可能要几分钟，还经常传到一半断。
 * 压到 600KB 左右，上传成功率和体感都完全不同。
 */
async function downscale(
  source: Blob,
  preset: CapturePreset,
): Promise<{ blob: Blob; mimeType: string; width: number; height: number }> {
  const bitmap = await createImageBitmap(source);
  const scale = Math.min(1, preset.maxEdge / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    throw new CameraError('FAILED');
  }
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', preset.quality),
  );
  if (!blob) throw new CameraError('FAILED');

  return { blob, mimeType: 'image/jpeg', width, height };
}

function toCameraError(error: unknown): CameraError {
  if (error instanceof CameraError) return error;
  const message = error instanceof Error ? error.message.toLowerCase() : '';
  if (message.includes('denied') || message.includes('permission')) return new CameraError('DENIED');
  if (message.includes('cancel')) return new CameraError('CANCELLED');
  return new CameraError('FAILED');
}
